import { useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type ToastType = 'success' | 'error' | 'loading';

interface ToastState {
  message: string;
  type: ToastType;
  visible: boolean;
}

export function useToast() {
  const [toast, setToast] = useState<ToastState>({ message: '', type: 'success', visible: false });

  useEffect(() => {
    if (toast.visible && toast.type !== 'loading') {
      const t = setTimeout(() => setToast(s => ({ ...s, visible: false })), 2500);
      return () => clearTimeout(t);
    }
  }, [toast.visible, toast.type]);

  const show = useCallback((message: string, type: ToastType) => {
    setToast({ message, type, visible: true });
  }, []);

  const hide = useCallback(() => {
    setToast(s => ({ ...s, visible: false }));
  }, []);

  return { toast, show, hide };
}

export function Toast({ message, type, visible }: ToastState) {
  if (!visible) return null;

  const Icon = type === 'loading' ? Loader2 : type === 'success' ? CheckCircle : XCircle;
  const color = type === 'loading' ? 'text-gold' : type === 'success' ? 'text-success' : 'text-danger';

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm shadow-xl">
      <Icon size={16} className={`${color} ${type === 'loading' ? 'animate-spin' : ''}`} />
      <span className="text-foreground">{message}</span>
    </div>
  );
}
